import React, { useState } from 'react';
import { AtSign, X } from 'lucide-react';
import { useCreateClientUserMutation, useGetOrganizationClientsQuery } from '../../endpoints/client.endpoints';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useDispatch } from 'react-redux';

interface CreateClientUserProps {
  onCancel: () => void;
}

const CreateClientUser: React.FC<CreateClientUserProps> = ({
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState('');
  const [currentInput, setCurrentInput] = useState('');
  const { activeClient, setClient } = useActiveClient();
  const dispatch = useDispatch();

  const [createClientUser, { isLoading }] = useCreateClientUserMutation();
  const { data: clients, refetch: refetchClientsList } = useGetOrganizationClientsQuery();
  const client = clients?.find(client => client.ext_client_id === activeClient?.ext_client_id);

  const handleSubmit = async(e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClient?.ext_client_id) {
      dispatch(showNotification({
        summary: 'No client selected',
        theme: 'warning',
      }));
      return;
    }
    for (const email of emails.split(',')) {
      const response = await createClientUser({
        clientId: activeClient?.ext_client_id,
        email: email,
      });

      if (!('data' in response)) {
        dispatch(showNotification({
          summary: `Error creating user ${email}`,
          theme: 'warning',
        }));
        return;
      }
    }

    dispatch(showNotification({
      summary: 'User created',
      theme: 'success',
    }));

    const { data: clientsList } = await refetchClientsList();
    if (clientsList && activeClient?.ext_client_id) {
      const updatedClient = clientsList.find(
        (client) => client.ext_client_id === activeClient.ext_client_id
      );
      if (updatedClient) {
        setClient(updatedClient);
      }
    }

    onCancel();
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row items-center text-xs text-gray-500">
          <AtSign className="h-3 w-3 mr-1" />
          Emails:
        </div>

        <div className="flex items-center space-x-2">

          <div className="flex-1 flex flex-wrap gap-1 p-1 rounded-none">
            {emails.split(',').map((e, i) => {
              const trimmedEmail = e.trim();
              if (!trimmedEmail) return null;

              const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

              return (
                <div
                  key={i}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                    isValidEmail ? 'bg-gray-201' : 'bg-red-200'
                  }`}
                >
                  <span>
                    {trimmedEmail}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const emailsList = emails.split(',');
                      emailsList.splice(i, 1);
                      setEmails(emailsList.join(','));
                    }}
                    className="hover:text-red-500"
                  >
                    <X className='size-3' />
                  </button>
                </div>
              );
            })}
            <div className="items-center">
              <input
                type="text"
                placeholder="Email addresses"
                value={currentInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setCurrentInput(value);

                  // Auto-add email when comma is typed
                  if (value.endsWith(',')) {
                    const emailToAdd = value.slice(0, -1).trim();
                    if (emailToAdd) {
                      setEmails(prev => prev ? `${prev},${emailToAdd}` : emailToAdd);
                      setCurrentInput('');
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = currentInput.trim();
                    if (value) {
                      setEmails(prev => prev ? `${prev},${value}` : value);
                      setCurrentInput('');
                    }
                  }
                }}
                onBlur={() => {
                  const value = currentInput.trim();
                  if (value) {
                    setEmails(prev => prev ? `${prev},${value}` : value);
                    setCurrentInput('');
                  }
                }}
                className="focus:ring-0 focus:outline-none shadow-none rounded-none pt-1"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-end mt-4 gap-2">
        <button
          type="button"
          onClick={onCancel}
          className=' bg-gray-200 text-gray-500 rounded-full py-1 px-4'
        >
          Cancel
        </button>
        <button
          type="submit"
          className='py-1 px-4 bg-green-501 text-white rounded-full py-1 px-4'
          onClick={handleSubmit}
        >
          Send Invites
        </button>
      </div>
    </>
  );
};

export default CreateClientUser;
